import type { OccursAction } from '@collector/sportsapi-types'

import { ChangeOrderIncident } from '../incidents/ChangeOrderIncident.js'
import {
  AddIncident,
  DeleteIncident,
  UpdateIncident,
  type AddIncidentPayload,
  type QueueIncidentError,
  type QueueIncidentStatus,
  type UpdateIncidentPayload,
} from '../incidents/index.js'
import type { QueueIncidentType } from '../incidents/types.js'
import type { QueueIncident } from '../types.js'

/**
 * Object representing data parsed from stringified QueueIncident
 */
export type RawQueueIncident = {
  action?: OccursAction
  error: QueueIncidentError
  eventId: number
  id?: number
  parentUuid?: string
  payload?: Record<string, unknown>
  status: QueueIncidentStatus
  time: number
  type: QueueIncidentType
  uuid?: string
}

/**
 * Convert raw queue incidents to queue incidents, which can be passed to EventIncidentsQueue to restore queue state
 */
export function parseRawQueueIncidents(
  rawQueueIncidents: RawQueueIncident[],
): QueueIncident[] {
  const queueIncidents = rawQueueIncidents.map((incident: RawQueueIncident) => {
    switch (incident.type) {
      case 'Add':
        return new AddIncident(
          incident.eventId,
          incident.payload as AddIncidentPayload,
          incident.uuid,
          incident.parentUuid,
          incident.status,
          incident.time,
          incident.error,
        )

      case 'Update':
        return new UpdateIncident(
          incident.eventId,
          incident.payload as UpdateIncidentPayload,
          incident.id!,
          incident.uuid,
          incident.status,
          incident.time,
          incident.error,
        )

      case 'Delete':
        return new DeleteIncident(
          incident.eventId,
          incident.id!,
          incident.uuid,
          incident.status,
          incident.time,
          incident.error,
        )

      case 'ChangeOrder':
        return new ChangeOrderIncident(
          incident.eventId,
          incident.id!,
          incident.action!,
          incident.status,
          incident.time,
          incident.error,
        )

      default:
        throw new Error(`incident type ${incident.type} not implemented`)
    }
  })

  return queueIncidents
}
